import { t } from "@/lib/i18n";
import { useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, ZoomControl, Circle, useMap } from "react-leaflet";
import { LocateFixed, Loader2, Crosshair } from "lucide-react";
import { categoryIcon, userIcon } from "./mapIcons";
import { OfficePopup } from "./OfficePopup";
import { CATEGORIES } from "@/lib/categories";

const FlyTo = ({ target, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo(target, zoom, { duration: 1.1 });
  }, [target, zoom, map]);
  return null;
};

const Legend = () => (
  <div className="absolute bottom-4 left-4 z-[500] hidden rounded-xl border border-white/10 bg-ac-glass p-3 backdrop-blur-xl xl:block">
    <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ac-accent">{t("map.layers")}</p>
    {Object.entries(CATEGORIES).map(([k, c]) => (
      <p key={k} className="flex items-center gap-2 py-0.5 text-xs text-slate-200">
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: c.color }} /> {c.label}
      </p>
    ))}
  </div>
);

const Hud = ({ geo, count }) => (
  <div data-testid="map-hud" className="absolute left-4 top-4 z-[500] flex flex-col gap-1 rounded-xl border border-white/10 bg-ac-glass px-3 py-2 font-mono text-[11px] text-slate-300 backdrop-blur-xl">
    <span className="flex items-center gap-2 text-ac-sky">
      <Crosshair className="h-3.5 w-3.5" />
      {geo.isReal ? t("map.gps") : t("map.default")}
    </span>
    <span>LAT {geo.position[0].toFixed(4)} · LNG {geo.position[1].toFixed(4)}</span>
    <span className="text-ac-accent-light">{t("map.points", { n: count })}</span>
  </div>
);

export const MapSection = ({ geo, offices, focus, hoverId, onAskAI }) => {
  const markerRefs = useRef({});

  useEffect(() => {
    if (focus?.id) setTimeout(() => markerRefs.current[focus.id]?.openPopup(), 1200);
  }, [focus]);

  return (
    <div data-testid="interactive-map" className="relative isolate h-full w-full overflow-hidden rounded-2xl border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,27,62,0.6)]">
      <MapContainer center={geo.position} zoom={13} scrollWheelZoom className="h-full w-full" zoomControl={false}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <ZoomControl position="topleft" />
        <FlyTo target={geo.position} zoom={13} />
        {focus && <FlyTo target={[focus.lat, focus.lng]} zoom={16} />}
        <Circle center={geo.position} radius={1500} pathOptions={{ color: "#10B981", weight: 1, fillOpacity: 0.06, dashArray: "4 6" }} interactive={false} />
        <Marker position={geo.position} icon={userIcon(t("map.you"))} interactive={false} keyboard={false} zIndexOffset={1000} />
        {offices.map((o) => (
          <Marker
            key={o.id}
            position={[o.lat, o.lng]}
            icon={categoryIcon(o.category, o.id === hoverId)}
            zIndexOffset={o.id === hoverId ? 2000 : 0}
            ref={(r) => (markerRefs.current[o.id] = r)}
          >
            <Popup>
              <OfficePopup office={o} onAskAI={onAskAI} />
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      <Hud geo={geo} count={offices.length} />
      <button
        data-testid="map-recalculate-location-button"
        onClick={() => geo.request(true)}
        className="absolute right-4 top-4 z-[500] flex items-center gap-2 rounded-full bg-ac-primary px-4 py-2.5 text-xs font-bold uppercase tracking-[0.12em] text-white shadow-lg transition-transform hover:-translate-y-0.5 active:scale-95"
      >
        {geo.status === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />}
        <span className="hidden sm:inline">{t("map.recalc")}</span>
      </button>
      <Legend />
    </div>
  );
};
